import { RestError } from '@azure/core-http';
import {
  KioskDto,
  KioskSettingsDto,
  SetKioskThemeDto,
  SetKioskThemeDtoTheme
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useApi } from '../../core/api/api.context';
import { QueryKeys } from '../../core/queries/query-keys.constants';

export function useQueryGetKiosk(kioskID?: string) {
  const api = useApi();

  return useQuery<KioskDto | undefined, RestError>(
    [QueryKeys.KIOSKS, kioskID],
    async () => {
      if (!kioskID) {
        return undefined;
      }
      return await api.kiosksController.get(kioskID);
    },
    { enabled: !!kioskID }
  );
}

export function useQueryGetKioskSettings(kioskID?: string) {
  const api = useApi();

  return useQuery<KioskSettingsDto | undefined, RestError>(
    [QueryKeys.KIOSKS, kioskID, 'settings'],
    async () => {
      if (!kioskID) return undefined;
      return await api.kiosksController.getSettings(kioskID);
    },
    { enabled: !!kioskID }
  );
}

export function useQueryGetKioskDefaultSettings() {
  const api = useApi();

  return useQuery<KioskSettingsDto, RestError>([QueryKeys.KIOSKS, 'settings-defaults'], async () => {
    return await api.kiosksController.getDefaultSettings();
  });
}

export function useMutationUpdateKioskSettings() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<KioskSettingsDto, RestError, { kioskID: string; dto: KioskSettingsDto; projectID?: string }>(
    async ({ kioskID, dto }) => {
      return await api.kiosksController.updateSettings(kioskID, dto);
    },
    {
      onSuccess(_, { kioskID, projectID }) {
        queryClient.invalidateQueries([QueryKeys.KIOSKS, kioskID]);
        if (projectID) {
          queryClient.invalidateQueries([QueryKeys.SETTINGS, projectID]);
        } else {
          queryClient.invalidateQueries([QueryKeys.SETTINGS]);
        }
      }
    }
  );
}

export function useMutationSetKioskTheme() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, RestError, { kioskID: string; theme: SetKioskThemeDtoTheme; projectID?: string }>(
    async ({ kioskID, theme }) => {
      const body: SetKioskThemeDto = { theme };
      await api.kiosksController.setTheme(kioskID, body);
    },
    {
      onSuccess(_, { kioskID, projectID }) {
        queryClient.invalidateQueries([QueryKeys.KIOSKS, kioskID]);
        if (projectID) {
          queryClient.invalidateQueries([QueryKeys.SETTINGS, projectID]);
        } else {
          queryClient.invalidateQueries([QueryKeys.SETTINGS]);
        }
      }
    }
  );
}
